import { useCallback } from 'react';

import { useToggleLikeMutation, useGetPromptQuery } from '../store';
import { useWithAuth } from './useWithAuth';

export type UseLikeParams = {
    promptId: number;
    initialLikes: number;
    initialIsLiked: boolean;
};

export type UseLikeReturn = {
    likes: number;
    isLiked: boolean;
    toggleLike: () => void;
    isLoading?: boolean;
};

/**
 * Хук для управления лайками через RTK Query
 * Автоматически синхронизирует состояние с кешем RTK Query
 */
export const useLike = ({ promptId, initialLikes, initialIsLiked }: UseLikeParams): UseLikeReturn => {
    const { withAuth } = useWithAuth();
    const [toggleLikeMutation, { isLoading }] = useToggleLikeMutation();

    // Получаем актуальные данные из кеша RTK Query
    const { data: cachedPrompt } = useGetPromptQuery(
        { promptId },
        {
            skip: false,
            // Не делаем новый запрос, используем только кеш
            refetchOnMountOrArgChange: false,
            refetchOnFocus: false,
            refetchOnReconnect: false,
        },
    );

    // Используем данные из кеша, если они есть, иначе fallback на initial значения
    const currentLikes = cachedPrompt?.likes ?? initialLikes;
    const currentIsLiked = cachedPrompt?.isLiked ?? initialIsLiked;

    const toggleLike = useCallback(async () => {
        if (isLoading) {
            return;
        }

        const newIsLiked = !currentIsLiked;

        await toggleLikeMutation({
            promptId,
            isLiked: newIsLiked,
        }).unwrap();
    }, [toggleLikeMutation, promptId, currentIsLiked, isLoading]);

    const toggleLikeWithAuth = useCallback(() => {
        withAuth(toggleLike, {
            retpath: `https://${window.location.hostname}/prompthub/prompts/${promptId}`,
        });
    }, [withAuth, toggleLike, promptId]);

    return {
        likes: currentLikes,
        isLiked: currentIsLiked,
        toggleLike: toggleLikeWithAuth,
        isLoading,
    };
};
